import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { joinCase } from '@/lib/ws/socket';
import { useConnectionStore } from '@/state/connection.store';
import {
  amendGroupVoucher,
  cancelGroupVoucher,
  cancelGroupVoucherCode,
  fetchGroupVoucher,
  issueGroupVoucher,
  syncGroupVoucherCodes,
} from './voucher-api';
import {
  groupVoucherKey,
  type AmendGroupVoucherInput,
  type GroupVoucherView,
  type IssueGroupVoucherInput,
} from './voucher-types';

// The PNR group's Uber voucher, live (#797).
//
// Same shape as `use-trips`: ONE seeding read, then the WS owns freshness —
// `TransportVoucherChanged` → `applyEvent` → `setQueryData(groupVoucherKey)`.
// No timer, no polling interval (frontend-conventions §15). A dropped socket is
// recovered the same way: re-join (idempotent) + exactly ONE catch-up refetch on
// the reconnect EVENT.

/** Fold a voucher view a write returned straight into the cache. */
function writeVoucher(qc: QueryClient, voucher: GroupVoucherView): void {
  qc.setQueryData<GroupVoucherView | null>(groupVoucherKey(voucher.subCaseUrn), voucher);
}

export function useGroupVoucher(caseUrn: string | undefined, subCaseUrn: string | undefined) {
  const active = !!caseUrn && !!subCaseUrn;

  const query = useQuery<GroupVoucherView | null>({
    queryKey: groupVoucherKey(subCaseUrn ?? ''),
    queryFn: () => fetchGroupVoucher(caseUrn as string, subCaseUrn as string),
    enabled: active,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!active || !caseUrn) return;
    joinCase(caseUrn);
  }, [active, caseUrn]);

  const connected = useConnectionStore((s) => s.connected);
  const wasConnected = useRef(connected);
  const { refetch } = query;
  useEffect(() => {
    const reconnected = connected && !wasConnected.current;
    wasConnected.current = connected;
    if (!reconnected || !active || !caseUrn) return;
    joinCase(caseUrn);
    void refetch();
  }, [connected, active, caseUrn, refetch]);

  return query;
}

export interface IssueGroupVoucherArgs {
  caseUrn: string;
  subCaseUrn: string;
  input: IssueGroupVoucherInput;
}

/** Issue the group's program (idempotent ensure at the BFF/domain). Fold-the-response-in. */
export function useIssueGroupVoucher() {
  const qc = useQueryClient();
  return useMutation<GroupVoucherView, Error, IssueGroupVoucherArgs>({
    mutationFn: ({ caseUrn, subCaseUrn, input }) => issueGroupVoucher(caseUrn, subCaseUrn, input),
    onSuccess: (voucher) => writeVoucher(qc, voucher),
  });
}

export interface AmendGroupVoucherArgs {
  caseUrn: string;
  subCaseUrn: string;
  input: AmendGroupVoucherInput;
}

/** In-place amendment (M4) — the program keeps its codes; fold-the-response-in. */
export function useAmendGroupVoucher() {
  const qc = useQueryClient();
  return useMutation<GroupVoucherView, Error, AmendGroupVoucherArgs>({
    mutationFn: ({ caseUrn, subCaseUrn, input }) => amendGroupVoucher(caseUrn, subCaseUrn, input),
    onSuccess: (voucher) => writeVoucher(qc, voucher),
  });
}

export interface CancelGroupVoucherInput {
  caseUrn: string;
  subCaseUrn: string;
  reason: string;
}

/** Cancel the program. Fold-the-response-in, same rule as the trip writes. */
export function useCancelGroupVoucher() {
  const qc = useQueryClient();
  return useMutation<GroupVoucherView, Error, CancelGroupVoucherInput>({
    mutationFn: ({ caseUrn, subCaseUrn, reason }) => cancelGroupVoucher(caseUrn, subCaseUrn, reason),
    onSuccess: (voucher) => writeVoucher(qc, voucher),
  });
}

export interface CancelGroupVoucherCodeInput {
  caseUrn: string;
  subCaseUrn: string;
  codeId: string;
  reason: string;
}

/** Cancel ONE code (#826) — folds the refreshed program straight into the cache. */
export function useCancelGroupVoucherCode() {
  const qc = useQueryClient();
  return useMutation<GroupVoucherView, Error, CancelGroupVoucherCodeInput>({
    mutationFn: ({ caseUrn, subCaseUrn, codeId, reason }) =>
      cancelGroupVoucherCode(caseUrn, subCaseUrn, codeId, reason),
    onSuccess: (voucher) => writeVoucher(qc, voucher),
  });
}

export interface SyncGroupVoucherInput {
  caseUrn: string;
  subCaseUrn: string;
}

/** Resume asynchronous code generation now. */
export function useSyncGroupVoucherCodes() {
  const qc = useQueryClient();
  return useMutation<GroupVoucherView, Error, SyncGroupVoucherInput>({
    mutationFn: ({ caseUrn, subCaseUrn }) => syncGroupVoucherCodes(caseUrn, subCaseUrn),
    onSuccess: (voucher) => writeVoucher(qc, voucher),
  });
}
